import type { PartnerCockpitSubscriptionCharge } from '@houzy/schemas/partner-cockpit/subscription';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { formatSwissAmount } from '../../../lib/formatSwissAmount';
import { formatSwissDate } from '../../../lib/time';
import {
    subscriptionChargeStatusLabel,
    subscriptionChargeTypeLabel,
    SUBSCRIPTION_CHARGE_STATUS_COLOR,
} from '../labels';

export type BillingTableProps = {
    charges: PartnerCockpitSubscriptionCharge[];
    onSelect: (charge: PartnerCockpitSubscriptionCharge) => void;
};

type ChargeView = {
    invoiceNumber: string;
    issuedAt: string;
    period: string;
    amount: string;
};

function chargeView(t: TFunction, charge: PartnerCockpitSubscriptionCharge): ChargeView {
    return {
        invoiceNumber: charge.invoiceNumber ?? '—',
        issuedAt: charge.issuedAt ? formatSwissDate(new Date(charge.issuedAt)) : '—',
        period: t('SETTINGS.SUBSCRIPTION.BILLING.PERIOD_RANGE', {
            from: formatSwissDate(new Date(charge.periodStart)),
            to: formatSwissDate(new Date(charge.periodEnd)),
        }),
        amount: formatSwissAmount(charge.totalAmount),
    };
}

// TODO: paginate when the catalogue turns monthly-heavy.
export function BillingTable({ charges, onSelect }: BillingTableProps): ReactElement {
    const { t } = useTranslation();
    const theme = useTheme();
    // Only one of the two renderings is built: `charges` is unpaginated, so a hidden copy is real work.
    // `noSsr` reads the query on the first render, otherwise a phone paints the table before swapping.
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

    if (charges.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t('SETTINGS.SUBSCRIPTION.BILLING.EMPTY')}
            </Typography>
        );
    }

    if (isNarrow) {
        return (
            <Stack spacing={1}>
                {charges.map((charge) => {
                    const view = chargeView(t, charge);
                    return (
                        <ButtonBase
                            key={charge.uuid}
                            onClick={() => onSelect(charge)}
                            aria-label={charge.invoiceNumber ?? t('SETTINGS.SUBSCRIPTION.BILLING.NO_NUMBER')}
                            sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 1 }}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {view.invoiceNumber}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {view.amount}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        {view.period}
                                    </Typography>
                                    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                        <Chip size="small" label={subscriptionChargeTypeLabel(t, charge)} />
                                        <Chip
                                            size="small"
                                            color={SUBSCRIPTION_CHARGE_STATUS_COLOR[charge.status]}
                                            label={subscriptionChargeStatusLabel(t, charge.status)}
                                        />
                                    </Stack>
                                </Stack>
                            </Paper>
                        </ButtonBase>
                    );
                })}
            </Stack>
        );
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>{t('SETTINGS.SUBSCRIPTION.BILLING.INVOICE')}</TableCell>
                    <TableCell>{t('SETTINGS.SUBSCRIPTION.BILLING.DATE')}</TableCell>
                    <TableCell>{t('SETTINGS.SUBSCRIPTION.BILLING.PERIOD')}</TableCell>
                    <TableCell>{t('SETTINGS.SUBSCRIPTION.BILLING.TYPE')}</TableCell>
                    <TableCell align="right">{t('SETTINGS.SUBSCRIPTION.BILLING.AMOUNT')}</TableCell>
                    <TableCell>{t('SETTINGS.SUBSCRIPTION.BILLING.STATUS')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {charges.map((charge) => {
                    const view = chargeView(t, charge);
                    return (
                        <TableRow
                            key={charge.uuid}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => onSelect(charge)}
                            aria-label={charge.invoiceNumber ?? t('SETTINGS.SUBSCRIPTION.BILLING.NO_NUMBER')}>
                            <TableCell>{view.invoiceNumber}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{view.issuedAt}</TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>{view.period}</TableCell>
                            <TableCell>
                                <Chip size="small" label={subscriptionChargeTypeLabel(t, charge)} />
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {view.amount}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    color={SUBSCRIPTION_CHARGE_STATUS_COLOR[charge.status]}
                                    label={subscriptionChargeStatusLabel(t, charge.status)}
                                />
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}
